import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { motion } from "framer-motion";
import axios from "axios";
import configServ from "../../services/config";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx"; // ⬅️ Add this at the top
const MotionBox = motion(Box);

const BulkItemsUploadDialog = ({ open, handleClose }) => {
  const { user_id, company_id } = useSelector((state) => state.GlobalVariables);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const expectedHeaders = [
    "item_name",
    "brand_name",
    "group_name",
    "category_name",
    "location_name",
    "costprice",
    "item_code",
    "type",
    "tax_rate",
    "unit",
    "openingquantity",
    "sku",
    "hsncode",
    "rate_unit",
    "packagingunit",
    "conversionfactor",
    "discount",
    "description",
    "subscriptionstatus", // ⬅️ converted to lowercase
    "istaxinclusive",        // ⬅️ already lowercase with underscore
  ];
  

  const validateExcelHeaders = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    const headers = jsonData[0].map((h) => h?.toString().trim().toLowerCase());

    const missingHeaders = expectedHeaders.filter(
      (expected) => !headers.includes(expected)
    );

    if (missingHeaders.length > 0) {
      // ❌ Detailed message showing missing headers
      toast.error(
        `Header mismatch! Missing or incorrect: ${missingHeaders.join(", ")}`,
        { duration: 6000 }
      );

      // 📎 Guidance to refer to sample format
      toast.error("Please use the sample format file to upload your items.", {
        duration: 5000,
      });

      throw new Error("Header mismatch. Upload aborted.");
    }
  };

  const handleFileClick = () => {
    fileInputRef.current.click(); // trigger the hidden input
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => resolve(reader.result); // base64 string
      reader.onerror = (error) => reject(error);
    });
  };

  function downloadBase64File(base64, filename) {
    const link = document.createElement("a");
    const mime =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const blob = base64ToBlob(base64, mime);
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function base64ToBlob(base64, mime) {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mime });
  }

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      // ✅ Validate headers
      await validateExcelHeaders(file);
      const base64 = await convertFileToBase64(file);

      const payload = {
        user_id: user_id,
        filename: file.name,
        file: base64,
        company_id: company_id,
      };

      const res = await configServ.bulkUploadItems(payload);

      if (res.status === 200) {
        toast.success("File uploaded successfully!");
        handleClose();
      } else if (res.status === 207) {
        toast.error("Some items failed to upload. Downloading error file...");

        if (res.failed_file_base64 && res.filename) {
          downloadBase64File(
            res.failed_file_base64,
            res.filename || "failed_Items.xlsx"
          );
        }
      } else {
        toast.error("Unexpected response from server.");
      }
    } catch (e) {
      console.log(e);
      toast.error("Upload failed!");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <MotionBox
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Bulk Items Upload
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            You can upload multiple Items using an Excel or CSV file.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudDownloadIcon />}
            href="/assets/sample_bulk_items.xlsx"
            download
          >
            Download Format
          </Button>

          <input
            type="file"
            accept=".xlsx,.csv"
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
          />

          <Button
            variant="contained"
            color="success"
            startIcon={
              uploading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CloudUploadIcon />
              )
            }
            onClick={handleFileClick}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Upload File"}
          </Button>
        </DialogActions>
      </MotionBox>
    </Dialog>
  );
};

export default BulkItemsUploadDialog;
